import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { CONTROLLER_CATALOG_ITEM_TYPES, ControllerCatalogItemType } from "../../../enums/controllerCatalogItemTypes";
import { ControllerCatalogItem } from "../../../models/api/fault/controllerCatalogItem";
import { ControllerManufacturer, ControllerManufacturerObject } from "../../../models/api/fault/controllerManufacturer";
import { ControllerModel, ControllerModelObject } from "../../../models/api/fault/controllerModel";

@Injectable({
  providedIn: "root",
})
export class ControllerCatalogService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Get controller catalog items by type and parentId.
   *
   * @param catalogType - Type of catalog items to retrieve.
   * @param parentId - Parent item id.
   * @returns Catalog items.
   */
  public getCatalogItems(
    catalogType: CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER,
    parentId?: string
  ): Observable<ControllerManufacturer[] | null>;
  public getCatalogItems(
    catalogType: CONTROLLER_CATALOG_ITEM_TYPES.MODEL,
    parentId?: string
  ): Observable<ControllerModel[] | null>;
  public getCatalogItems(catalogType: string, parentId?: string): Observable<ControllerCatalogItem[] | null>;
  public getCatalogItems(catalogType: string, parentId?: string): Observable<ControllerCatalogItem[] | null> {
    let params: HttpParams = new HttpParams();
    params = params.set("catalogType", catalogType);

    if (parentId) {
      params = params.set("parentId", parentId);
    }

    return this.http
      .get<ControllerManufacturerObject[] | ControllerModelObject[]>(environment.baseUrlV2 + "/controllerCatalogItem", {
        params,
      })
      .pipe(
        this.apiErrorHandler.handle(catalogType.toLowerCase()),
        map((res) => {
          const catalogItems = this.convertToCatalogItem(catalogType, res);
          if (catalogItems) {
            return this.orderByLabel(catalogItems);
          } else {
            return null;
          }
        })
      );
  }

  /**
   * Convert server response body to catalog item.
   *
   * @param catalogType - Type of catalog items.
   * @param serverResBody - Server response body.
   * @returns Catalog items.
   */
  public convertToCatalogItem(
    catalogType: string,
    serverResBody: ControllerManufacturerObject[] | ControllerModelObject[] | null
  ): ControllerCatalogItem[] | null {
    if (!serverResBody) return null;
    switch (catalogType) {
      case CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER: {
        const manufacturers: ControllerManufacturer[] = [];
        const manufacturerBody = serverResBody as ControllerManufacturerObject[];
        manufacturerBody.forEach((manufacturer) => {
          if (manufacturer) {
            manufacturers.push(new ControllerManufacturer(manufacturer));
          }
        });
        return manufacturers;
      }
      case CONTROLLER_CATALOG_ITEM_TYPES.MODEL: {
        const models: ControllerModel[] = [];
        const modelsBody = serverResBody as ControllerModelObject[];
        modelsBody.forEach((model) => {
          if (model) {
            models.push(new ControllerModel(model));
          }
        });
        return models;
      }
      default:
        return null;
    }
  }

  /**
   * Get empty catalog item.
   *
   * @param catalogType - Type of catalog items.
   * @param parentId - Parent item id.
   * @returns Empty catalog item.
   */
  public getEmptyCatalogItem(catalogType: ControllerCatalogItemType, parentId?: string): ControllerCatalogItem | null {
    let catalogItem: ControllerCatalogItem;
    switch (catalogType) {
      case CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER:
        catalogItem = new ControllerManufacturer({ label: "" });
        break;
      case CONTROLLER_CATALOG_ITEM_TYPES.MODEL:
        catalogItem = parentId ? new ControllerModel({ label: "", manufacturer: { id: parentId } }) : null;
        break;
      default:
        catalogItem = null;
        break;
    }
    return catalogItem;
  }

  /**
   * Add new catalog item.
   *
   * @param catalogType - Type of catalog items.
   * @param catalogItem - Catalog item to add.
   * @returns Server response.
   */
  public addCatalogItem(catalogType: string, catalogItem: ControllerCatalogItem) {
    let params: HttpParams = new HttpParams();
    params = params.set("catalogType", catalogType);

    return this.http
      .post(environment.baseUrlV2 + "/controllerCatalogItem", catalogItem?.values, {
        observe: "response",
        params,
      })
      .pipe(this.apiErrorHandler.handle(catalogType.toLowerCase()));
  }

  /**
   * Edit existing catalog item.
   *
   * @param catalogType - Type of catalog items.
   * @param catalogItem - Catalog item to edit.
   * @returns Server response.
   */
  public editCatalogItem(catalogType: string, catalogItem: ControllerCatalogItem) {
    const catalogItemId = catalogItem?.id;
    delete catalogItem?.id;
    let params = new HttpParams();
    params = params.set("catalogType", catalogType);

    return this.http
      .put(environment.baseUrlV2 + "/controllerCatalogItem/" + catalogItemId, catalogItem?.values, {
        observe: "response",
        params,
      })
      .pipe(this.apiErrorHandler.handle(catalogType.toLowerCase()));
  }

  /**
   * Delete existing catalog item.
   *
   * @param catalogType - Type of catalog items.
   * @param catalogItem - Catalog item to delete.
   * @returns Server response.
   */
  public deleteCatalogItem(catalogType: string, catalogItem: ControllerCatalogItem) {
    let params = new HttpParams();
    params = params.set("catalogType", catalogType);

    return this.http
      .delete(environment.baseUrlV2 + "/controllerCatalogItem/" + catalogItem?.id, {
        observe: "response",
        params,
      })
      .pipe(this.apiErrorHandler.handle(catalogType.toLowerCase()));
  }

  /**
   * Order catalog items by label.
   *
   * @param catalogItems - Items to order.
   * @returns Ordered items.
   */
  private orderByLabel(catalogItems: ControllerCatalogItem[]): ControllerCatalogItem[] {
    return catalogItems.sort((a, b) => {
      if (a?.label && b?.label) {
        return a.label.localeCompare(b.label);
      } else {
        return 0;
      }
    });
  }
}
